import styled from 'styled-components';
import { templateSlotStyles } from 'views/components/calendar/templateSlot.style';

const AXON = '#6f42f5';

/**
 * Two-column layout for the provider details page:
 * a fixed-width left info sidebar + a flexible main column
 * (breadcrumb, action bar, calendar). Matches the redesigned
 * "Provider Page - Week/Day View" frames in Figma.
 */
export const ProfileLayout = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: calc(100vh - 64px);
  background: #ffffff;

  .profile-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  /* breadcrumb sits inside the main column */
  .profile-main .bread-crumbs {
    padding: 20px 32px 0;
  }

  .profile-main .bread-crumbs .inner-wrap {
    max-width: none;
    margin: 0;
    padding: 0;
  }

  /* New Appointment / Manage Availability live in the calendar toolbar's
     right slot so they share a single row with the Day/Week toggle and the
     Today / date navigation controls. */
  .profile-main .toolbar-right-actions {
    display: flex;
    align-items: center;
    margin-left: auto;
  }
  .profile-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .profile-actions .btn-new-appt {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 16px;
    border-radius: 6px;
    background: ${AXON};
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    line-height: 40px;
  }
  .profile-actions .btn-new-appt:hover {
    color: #ffffff;
    opacity: 0.92;
  }
  .profile-actions .btn-manage-availability {
    display: inline-flex;
    align-items: center;
    height: 40px;
    padding: 0 16px;
    border-radius: 6px;
    background: #ffffff !important;
    color: ${AXON} !important;
    border: 1px solid ${AXON} !important;
    font-size: 14px;
    font-weight: 500;
    box-shadow: none;
  }
  .profile-actions .btn-manage-availability:hover,
  .profile-actions .btn-manage-availability:focus {
    background: ${AXON} !important;
    color: #ffffff !important;
    border-color: ${AXON} !important;
  }

  /* let the schedule calendar fill the main column */
  .profile-main .schedule-calendar {
    max-width: none !important;
    margin: 0 !important;
    padding: 8px 32px 24px !important;
  }
  .profile-main .schedule-calendar .calendar-nav {
    left: 0 !important;
  }
  /* New (DT) template hides the Day/Week toggle + Today/date navigation
     (the old template keeps them). */
  .profile-main .calendar-switch,
  .profile-main .calendar-nav {
    display: none !important;
  }
  /* Remove the Sunday column (first day) from the week view */
  .profile-main .rbc-time-header-content .rbc-header:first-child {
    display: none !important;
  }
  .profile-main .rbc-time-content > .rbc-time-column:nth-child(2) {
    display: none !important;
  }
  /* Saturday: header stays white, only the body column (8 AM-8 PM) is grey */
  .profile-main .rbc-time-content > .rbc-time-column:last-child {
    background-color: #ebebeb !important;
  }

  /* ---- custom day-column headers (week/day views) ---- */
  .profile-main .rbc-time-header-content {
    background: #ffffff !important;
  }
  .profile-main .rbc-time-header-cell .rbc-header {
    border-color: #e8e8e8;
    padding: 8px 0;
  }
  .profile-main .provider-col-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }
  .profile-main .provider-col-header .dow {
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #545454;
  }
  /* Booked vs the care sessions the template defines for that day. Same rule as
     the planner badge: short of the template count is red, all filled green,
     and neutral when the template defines none. */
  .profile-main .provider-col-header .sess {
    font-size: 11px;
    font-weight: 600;
    color: #545454;
    padding: 1px 8px;
    border-radius: 10px;
    line-height: 16px;
    white-space: nowrap;
  }
  .profile-main .provider-col-header .sess.under {
    background: #f4b3b3;
    color: #21303a;
  }
  .profile-main .provider-col-header .sess.met {
    background: #d3e9d5;
    color: #21303a;
  }
  .profile-main .provider-time-gutter {
    text-align: center;
    padding-top: 10px;
    font-size: 13px;
    font-weight: 500;
    color: #545454;
  }
  /* Which clock the grid is on. Same pill as .sess above — and the same
     #21303a on a pale fill — but in the page accent rather than red or green,
     which on this header already mean short of target and fully booked. */
  .profile-main .template-title {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .profile-main .template-title .tz-chip {
    font-size: 11px;
    font-weight: 600;
    color: #21303a;
    background: rgba(111, 66, 245, 0.1);
    padding: 1px 8px;
    border-radius: 10px;
    line-height: 16px;
    white-space: nowrap;
  }

  /* ---- calendar grid borders / 30-min rows (match Figma) ---- */
  /* The shared BigCalendar wrapper removes the time-view border and pins it
     to a fixed 1245px width at equal specificity, so override with !important
     to restore the outer frame and let it fill the column. */
  .profile-main .rbc-time-view {
    border: 1px solid #b9c0c9 !important;
    width: 100% !important;
    margin: 0 !important;
  }
  /* Insurance: draw the outer top/left directly on the header + body too, in
     case the shared BigCalendar wrapper still wins on the time-view border. */
  .profile-main .rbc-time-header.rbc-overflowing {
    border: none !important;
    border-top: 1px solid #b9c0c9 !important;
    border-left: 1px solid #b9c0c9 !important;
    /* grey band between the day headers and the 8 AM grid (all columns) */
    border-bottom: 18px solid #ebebeb !important;
    margin-right: 0 !important;
  }
  .profile-main .rbc-time-content {
    border-top: 1px solid #b9c0c9 !important;
    border-left: 1px solid #b9c0c9 !important;
  }
  /* highlight the grey band with a top line across PDT (gutter) -> Sat */
  .profile-main .rbc-time-header-content,
  .profile-main .rbc-time-header-gutter {
    border-bottom: 1px solid #b9c0c9 !important;
  }
  /* separator between the time gutter and the day columns */
  .profile-main .rbc-time-header-gutter,
  .profile-main .rbc-time-gutter {
    border-right: 1px solid #e0e0e0 !important;
  }
  /* drop the wrapper's 20px top padding so the header cell borders reach the
     top frame border */
  .profile-main .rbc-time-header {
    padding-top: 0 !important;
    margin-bottom: 0 !important;
  }
  /* center the CDT label in the header gutter cell */
  .profile-main .rbc-time-header-gutter {
    align-items: center !important;
  }
  /* vertical separators between day-column headers */
  .profile-main .rbc-time-header-cell .rbc-header {
    border-left: 1px solid #e0e0e0;
  }
  .profile-main .rbc-time-header-cell .rbc-header:first-child {
    border-left: none;
  }
  /* vertical separators between day columns in the grid body */
  .profile-main .rbc-time-content > * + * {
    border-left: 1px solid #e0e0e0;
  }
  /* one slot per 30-min row: no intra-row sub-line (avoids the double border) */
  .profile-main .rbc-time-slot {
    border-top: none !important;
  }
  /* vertically center the time labels within each gutter row */
  .profile-main .rbc-time-gutter .rbc-time-slot {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  /* Calendar events - Figma "Provider Page - Week View" */
  .profile-main .rbc-event.na {
    background: #ebebeb !important;
    background-image: none !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }
  /* Every rule describing how a template slot looks now lives in one place,
     shared with the clinic-wide day grid so the two views cannot drift. */
  ${templateSlotStyles('.profile-main')}
`;
